/* eslint-disable @typescript-eslint/no-empty-function */
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { debouncedSearchLatest } from '@vsolv/utils/rxjs-operators';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { Distributor } from '@wsphere/distributors/domain';
import { SecurityService } from '@wsphere/staff/web';
import { BehaviorSubject, Observable, ReplaySubject, merge, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { DistributorService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-distributor-picker',
  templateUrl: './distributor-picker.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DistributorPickerComponent,
    },
  ],
})
export class DistributorPickerComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  constructor(
    public elementRef: ElementRef,
    private securitySvc: SecurityService,
    private distributorSvc: DistributorService
  ) {}

  @PropertyListener('value') value$ = new ReplaySubject<Distributor.Model>(1);
  @Input() value: Distributor.Model | null = null;

  @Input() placeholder = 'Find a distributor...';
  @Input() required = false;
  @Input() disabled = false;
  @Input() onlyActive = false;
  @Input() onbordingLinkView = false;
  @Input() includeSubDistributors = false;

  @Input() showClearButton = false;
  @Input() clearButtonText = 'Clear';

  @Input() themeColor: ThemeColor = 'primary';

  @Output() openChanged = new EventEmitter<boolean>();

  @Output() search = new EventEmitter<string>();
  @Output() valueChanges = new EventEmitter<Distributor.Model | null>();

  globalDistributors$ = this.securitySvc.globalDistributors$;

  readonly searchQuery$ = new BehaviorSubject<string>('');
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loadingSubject.asObservable();

  touched = false;
  overlayOpen = false;

  private _onNgChanges$ = new BehaviorSubject<null>(null);

  private readonly searchTriggers$ = merge(
    this.searchQuery$,
    this._onNgChanges$.pipe(map(() => this.searchQuery$.value)),
    this.globalDistributors$.pipe(map(() => this.searchQuery$.value))
  );

  distributors$: Observable<Distributor.Model[]> = this.searchTriggers$.pipe(
    debouncedSearchLatest(
      (searchTerm: string) =>
        this.globalDistributors$.pipe(
          take(1),
          tap(() => this.loadingSubject.next(true)),
          switchMap(() => {
            const options: Distributor.ListDistributorsQueryRequest = {
              ...(this.onlyActive && { status: Distributor.Status.ACTIVE }),
              includeSubDistributors: this.includeSubDistributors,
              ...(searchTerm && { search: searchTerm }),
            };

            return this.onbordingLinkView
              ? this.distributorSvc.listDistributorsForOnboarding$(options)
              : this.distributorSvc.listDistributors$(options);
          }),
          map(response => response.items.filter(distributor => distributor.id !== this.value?.id)),
          map(distributors => (this.value ? [this.value, ...distributors] : distributors)),
          tap(() => this.loadingSubject.next(false)),
          catchError(() => {
            this.loadingSubject.next(false);
            return of([] as Distributor.Model[]);
          })
        ),
      { debounceMs: 300 }
    )
  );

  @Input() compareWith: (v0: Distributor.Model | null, v1: Distributor.Model | null) => boolean = (
    v0: Distributor.Model | null,
    v1: Distributor.Model | null
  ) => {
    switch (typeof v0) {
      case 'object':
        return JSON.stringify(v0) === JSON.stringify(v1);
      default:
        return v0 === v1;
    }
  };

  onChange = (_value: Distributor.Model | null) => {};
  onTouched = () => {};

  ngOnChanges() {
    this._onNgChanges$.next(null);
  }

  selectValueChanged(value: Distributor.Model | null) {
    this.markAsTouched();
    this.value = value;
    // The input shows the distributor's name, not the search term, so a leftover query would
    // silently keep the next dropdown filtered to whatever was typed last.
    this.searchQuery$.next('');
    this.close();
    this.onChange(this.value);
    this.valueChanges.next(this.value);
  }

  searchFor(search: string) {
    this.search.emit(search);
    this.searchQuery$.next(search);
  }

  open() {
    this.overlayOpen = true;
  }

  close() {
    this.overlayOpen = false;
  }

  toggle() {
    this.overlayOpen = !this.overlayOpen;
  }

  writeValue(value: Distributor.Model | null): void {
    this.value = value;
  }

  registerOnChange(onChange: (_value: Distributor.Model | null) => {}): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => {}): void {
    this.onTouched = onTouched;
  }

  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  ngOnDestroy(): void {
    this.value$.complete();
    this.searchQuery$.complete();
    this.loadingSubject.complete();
  }
}
